import React, { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Codes cadeaux et codes de réduction.
 *
 * ── Deux natures, et elles ne se ressemblent pas ────────────────────────────
 *
 * Une RÉDUCTION allège un paiement : elle existe chez Stripe (coupon +
 * promotion code), s'applique au tunnel de paiement, et c'est Stripe qui
 * prélève le montant remisé et l'inscrit sur la facture. Un CADEAU ouvre
 * l'accès sans paiement du tout — ni carte, ni facture. Le client ne les
 * saisit d'ailleurs pas au même endroit.
 *
 * ── Ce que cet écran ne peut PAS faire, et pourquoi ─────────────────────────
 *
 * Modifier le montant d'une remise, sa durée ou la chaîne du code : ces
 * trois-là vivent aussi chez Stripe, dans des objets qui ne se modifient pas.
 * Les changer voudrait dire créer un second coupon et un second code en
 * laissant derrière celui que des clients ont peut-être déjà utilisé — deux
 * codes pour une campagne, dont un fantôme. Pour changer une remise, on ferme
 * le code et on en ouvre un autre.
 *
 * Fermer est définitif pour la même raison : la contrepartie Stripe est
 * supprimée, et le portail refuse la réouverture. Les abonnements qui
 * bénéficient déjà de la remise la conservent — chez Stripe, une remise
 * accordée vit sur l'abonnement, pas sur le coupon.
 *
 * ── Un code appartient à UN produit ────────────────────────────────────────
 *
 * Le portail refuse qu'un code couvre une formule d'un autre produit que le
 * sien. Le sélecteur ci-dessous choisit donc à la fois ce qu'on regarde et ce
 * qu'on crée : il n'existe pas de code valable sur toute la suite.
 */

const NATURES = [
  { id: 'DISCOUNT', label: 'Réduction' },
  { id: 'GIFT', label: 'Cadeau' },
];

const EMPTY = {
  code: '',
  label: '',
  kind: 'DISCOUNT',
  planIds: [],
  mode: 'POURCENT',
  valeur: '',
  durationMonths: '',
  giftMonths: '3',
  // Durée du cadeau en mois ou en jours (14/10/2026 : essai bêta de 15 jours).
  giftUnite: 'MOIS',
  giftDays: '15',
  giftQuantity: '1',
  // Modèle de codes personnels, tirés par le site semply.fr.
  personalTemplate: false,
  personalValidityDays: '30',
  maxRedemptions: '',
  expiresAt: '',
  notes: '',
};

const dateCourte = (iso) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * La règle d'un code, en une phrase.
 *
 * Recopiée du portail (`PromoService.resumer`) plutôt qu'appelée : le
 * back-office ne dépend d'aucun paquet partagé avec l'API. Les deux doivent
 * dire la même chose — si l'une change, l'autre suit.
 */
function resumer(code) {
  if (code.kind === 'GIFT' && code.giftDays) {
    const j = code.giftDays;
    const quantite = code.giftQuantity > 1 ? `, ${code.giftQuantity} unités` : '';
    return `${j} jour${j > 1 ? 's' : ''} offert${j > 1 ? 's' : ''}${quantite}`;
  }
  if (code.kind === 'GIFT') {
    const mois = code.giftMonths ?? 0;
    const quantite = code.giftQuantity > 1 ? `, ${code.giftQuantity} unités` : '';
    return `${mois} mois offert${mois > 1 ? 's' : ''}${quantite}`;
  }
  const remise =
    code.percentOff != null
      ? `−${code.percentOff} %`
      : `−${Number(code.amountOff ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
  if (code.durationMonths == null) return `${remise} sur toute la durée de l’abonnement`;
  if (code.durationMonths <= 1) return `${remise} le premier mois`;
  return `${remise} pendant ${code.durationMonths} mois`;
}

/**
 * @param {object} props
 * @param {(path: string, options?: object) => Promise<unknown>} props.call
 *   Appel au PORTAIL, fourni par la page hôte : c'est lui qui porte le jeton et
 *   traduit les refus d'habilitation. En redéclarer un ici ferait deux
 *   traductions du même 403.
 * @param {Array} props.plans        Formules de la suite, déjà chargées.
 * @param {Array} props.products     `[{ id, label }]`, l'ordre de la page hôte.
 * @param {boolean} props.canWrite   Profil de consultation : rien à proposer.
 */
export function AdminPromoCodes({ call, plans, products, canWrite }) {
  const [product, setProduct] = useState(products[0]?.id ?? 'APP');
  const [codes, setCodes] = useState([]);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  const charger = useCallback(
    () =>
      call(`/admin/promo-codes?product=${product}`)
        .then((liste) => {
          setCodes(liste);
          setError('');
        })
        .catch((e) => setError(String(e.message ?? e))),
    [call, product],
  );

  /**
   * Le drapeau `vivant` n'est pas une précaution de principe : changer de
   * produit deux fois de suite lance deux requêtes, et rien ne garantit qu'elles
   * reviennent dans l'ordre. Sans lui, la réponse la plus lente écrase la plus
   * récente, et l'écran affiche les codes d'un produit sous le nom d'un autre.
   */
  useEffect(() => {
    let vivant = true;
    call(`/admin/promo-codes?product=${product}`)
      .then((liste) => {
        if (!vivant) return;
        setCodes(liste);
        setError('');
      })
      .catch((e) => {
        if (vivant) setError(String(e.message ?? e));
      });
    return () => {
      vivant = false;
    };
  }, [call, product]);

  /**
   * Changer de produit ferme ce qui appartenait au précédent.
   *
   * Fait ICI plutôt que dans un effet sur `product` : le détail ouvert et le
   * formulaire en cours ne sont pas des états à synchroniser avec le serveur,
   * ce sont les conséquences d'un clic. Les remettre à zéro dans un effet
   * déclencherait un rendu en cascade — et laisserait, l'espace d'une image, un
   * formulaire de création pointant sur les formules d'un autre produit.
   */
  const changerProduit = (id) => {
    setProduct(id);
    setDetail(null);
    setForm(null);
  };

  /** Les formules du produit qu'un code peut couvrir : les payantes. */
  const vendables = useMemo(
    () => plans.filter((p) => p.product === product && !p.gratuite),
    [plans, product],
  );

  async function agir(action, message) {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await action();
      await charger();
      setNotice(message);
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setSaving(false);
    }
  }

  const cadeau = form?.kind === 'GIFT';
  // Un cadeau ouvre UNE formule : il n'y a pas de choix du client derrière,
  // puisqu'il n'y a pas de tunnel de paiement. En cocher plusieurs laisserait
  // le portail prendre la première, ce qui est un tirage au sort déguisé.
  const tropDeFormules = cadeau && (form?.planIds.length ?? 0) > 1;

  const enregistrer = () =>
    agir(
      () =>
        call('/admin/promo-codes', {
          method: 'POST',
          body: JSON.stringify({
            code: form.code.trim().toUpperCase(),
            label: form.label.trim(),
            product,
            kind: form.kind,
            planIds: form.planIds,
            percentOff:
              form.kind === 'DISCOUNT' && form.mode === 'POURCENT' ? Number(form.valeur || 0) : null,
            amountOff:
              form.kind === 'DISCOUNT' && form.mode === 'MONTANT' ? Number(form.valeur || 0) : null,
            durationMonths:
              form.kind === 'DISCOUNT' && form.durationMonths ? Number(form.durationMonths) : null,
            giftMonths:
              form.kind === 'GIFT' && form.giftUnite === 'MOIS' ? Number(form.giftMonths || 0) : null,
            giftDays:
              form.kind === 'GIFT' && form.giftUnite === 'JOURS' ? Number(form.giftDays || 0) : null,
            giftQuantity: form.kind === 'GIFT' ? Number(form.giftQuantity || 1) : null,
            personalTemplate: form.kind === 'GIFT' && form.personalTemplate,
            personalValidityDays:
              form.kind === 'GIFT' && form.personalTemplate
                ? Number(form.personalValidityDays || 30)
                : null,
            maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
            expiresAt: form.expiresAt || null,
            notes: form.notes.trim() || null,
          }),
        }),
      `Code « ${form.code.trim().toUpperCase()} » créé.`,
    ).then(() => setForm(null));

  return (
    <div className="ap-promo">
      <div className="ap-promo-head">
        <label>
          Produit{' '}
          <select value={product} onChange={(e) => changerProduit(e.target.value)}>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        {canWrite && (
          <button
            className="ap-btn primary"
            disabled={saving || vendables.length === 0}
            onClick={() => setForm(form ? null : { ...EMPTY })}
          >
            {form ? 'Annuler' : '+ Nouveau code'}
          </button>
        )}
      </div>

      {error && <div className="ap-error">{error}</div>}
      {notice && <div className="ap-notice">{notice}</div>}
      {vendables.length === 0 && (
        <p className="ap-muted">
          Aucune formule payante pour ce produit : un code ne peut pas couvrir la version
          gratuite, qui n’est ni vendue ni facturée.
        </p>
      )}

      {form && (
        <section className="ap-panel">
          <h3>Nouveau code</h3>

          <div className="ap-promo-natures">
            {NATURES.map((n) => (
              <label key={n.id}>
                <input
                  type="radio"
                  checked={form.kind === n.id}
                  onChange={() => setForm({ ...form, kind: n.id, planIds: [] })}
                />{' '}
                {n.label}
              </label>
            ))}
          </div>
          <p className="ap-muted small">
            {cadeau
              ? 'Ouvre l’accès pour une durée, sans paiement ni carte. Rien n’est facturé, et l’accès se ferme seul au terme.'
              : 'Allège le paiement. Le client la saisit au moment de souscrire ; Stripe applique la remise et l’inscrit sur la facture.'}
          </p>

          <label>
            Code saisi par le client
            <input
              value={form.code}
              maxLength={40}
              placeholder="RENTREE2026"
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            />
          </label>

          <label>
            Libellé interne
            <input
              value={form.label}
              maxLength={150}
              placeholder="Campagne de rentrée"
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </label>

          {cadeau ? (
            <>
              <label>
                Durée offerte
                <span className="ap-promo-remise">
                  <input
                    type="number"
                    min={1}
                    value={form.giftUnite === 'JOURS' ? form.giftDays : form.giftMonths}
                    onChange={(e) =>
                      setForm(
                        form.giftUnite === 'JOURS'
                          ? { ...form, giftDays: e.target.value }
                          : { ...form, giftMonths: e.target.value },
                      )
                    }
                  />
                  <select
                    value={form.giftUnite}
                    onChange={(e) => setForm({ ...form, giftUnite: e.target.value })}
                  >
                    <option value="MOIS">mois</option>
                    <option value="JOURS">jours</option>
                  </select>
                </span>
              </label>
              <label className="ap-check">
                <input
                  type="checkbox"
                  checked={form.personalTemplate}
                  onChange={(e) => setForm({ ...form, personalTemplate: e.target.checked })}
                />{' '}
                Modèle de codes personnels
                <small className="ap-muted">
                  Le code ci-dessus ne se saisit pas : le site semply.fr en tire un code par
                  adresse e-mail (ex. « {form.code || 'BETA'}-K7QM4XPD »), réservé à cette adresse.
                  Le nombre d’utilisations devient le nombre de codes à émettre.
                </small>
              </label>
              {form.personalTemplate && (
                <label>
                  Délai pour activer un code reçu (jours)
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={form.personalValidityDays}
                    onChange={(e) => setForm({ ...form, personalValidityDays: e.target.value })}
                  />
                </label>
              )}
              <label>
                Quantité offerte
                <input
                  type="number"
                  min={1}
                  value={form.giftQuantity}
                  onChange={(e) => setForm({ ...form, giftQuantity: e.target.value })}
                />
              </label>
            </>
          ) : (
            <>
              <label>
                Remise
                <span className="ap-promo-remise">
                  <select
                    value={form.mode}
                    onChange={(e) => setForm({ ...form, mode: e.target.value })}
                  >
                    <option value="POURCENT">en %</option>
                    <option value="MONTANT">en €</option>
                  </select>
                  <input
                    type="number"
                    min={1}
                    max={form.mode === 'POURCENT' ? 100 : undefined}
                    step={form.mode === 'POURCENT' ? 1 : 0.01}
                    value={form.valeur}
                    onChange={(e) => setForm({ ...form, valeur: e.target.value })}
                  />
                </span>
              </label>
              <label>
                Pendant combien de mois
                <input
                  type="number"
                  min={1}
                  placeholder="toute la durée"
                  value={form.durationMonths}
                  onChange={(e) => setForm({ ...form, durationMonths: e.target.value })}
                />
                <small className="ap-muted">Vide = tant que l’abonnement dure. 1 = le premier mois.</small>
              </label>
            </>
          )}

          <label>
            Nombre d’utilisations
            <input
              type="number"
              min={1}
              placeholder="sans limite"
              value={form.maxRedemptions}
              onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
            />
            <small className="ap-muted">Un même compte ne peut de toute façon l’utiliser qu’une fois.</small>
          </label>

          <label>
            Expire le
            <input
              type="datetime-local"
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            />
          </label>

          <label>
            Note interne
            <input
              value={form.notes}
              maxLength={500}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>

          <fieldset className="ap-promo-plans">
            <legend>Formules couvertes</legend>
            {vendables.map((p) => (
              <label key={p.id}>
                <input
                  type="checkbox"
                  checked={form.planIds.includes(p.id)}
                  onChange={() =>
                    setForm({
                      ...form,
                      planIds: form.planIds.includes(p.id)
                        ? form.planIds.filter((x) => x !== p.id)
                        : [...form.planIds, p.id],
                    })
                  }
                />{' '}
                {p.name}
                {!p.active && <span className="ap-muted small"> — inactive, donc invendable</span>}
              </label>
            ))}
            {tropDeFormules && (
              <p className="ap-error">Un cadeau ouvre une seule formule : n’en cochez qu’une.</p>
            )}
          </fieldset>

          <div className="ap-actions">
            <button
              className="ap-btn primary"
              disabled={saving || form.planIds.length === 0 || tropDeFormules}
              onClick={enregistrer}
            >
              {saving ? 'Enregistrement…' : 'Créer le code'}
            </button>
            <button className="ap-btn" disabled={saving} onClick={() => setForm(null)}>
              Annuler
            </button>
          </div>
        </section>
      )}

      {codes.length === 0 ? (
        <p className="ap-muted">Aucun code pour ce produit.</p>
      ) : (
        <ul className="ap-promo-list">
          {codes.map((code) => {
            const utilise = code._count?.redemptions ?? code.redeemedCount;
            const emis = code._count?.personalCodes ?? 0;
            const epuise = code.maxRedemptions !== null && code.redeemedCount >= code.maxRedemptions;
            const expire = Boolean(code.expiresAt && new Date(code.expiresAt) <= new Date());

            return (
              <li key={code.id} className="ap-promo-item">
                <div>
                  <div className="ap-promo-titre">
                    <code>{code.code}</code>
                    <span className="ap-tag">{code.kind === 'GIFT' ? 'cadeau' : 'remise'}</span>
                    {code.personalTemplate && <span className="ap-tag">modèle personnel</span>}
                    {!code.active && <span className="ap-tag muted">fermé</span>}
                    {code.active && expire && <span className="ap-tag">expiré</span>}
                    {code.active && !expire && epuise && <span className="ap-tag">épuisé</span>}
                  </div>
                  <p>
                    {code.label} — <strong>{resumer(code)}</strong>
                  </p>
                  <p className="ap-muted small">
                    {[
                      code.personalTemplate
                        ? `${code.issuedCount ?? emis} code${(code.issuedCount ?? emis) > 1 ? 's' : ''} émis`
                        : `${utilise} utilisation${utilise > 1 ? 's' : ''}`,
                      code.maxRedemptions !== null ? `sur ${code.maxRedemptions}` : 'sans limite',
                      code.expiresAt ? `jusqu’au ${dateCourte(code.expiresAt)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <p className="ap-muted small">
                    Formules : {code.plans.map((p) => p.plan.name).join(', ') || 'aucune'}
                  </p>
                  {code.notes && <p className="ap-muted small">{code.notes}</p>}
                </div>

                <div className="ap-promo-actions">
                  <button
                    className="ap-btn"
                    disabled={saving}
                    onClick={() =>
                      detail?.id === code.id
                        ? setDetail(null)
                        : call(`/admin/promo-codes/${code.id}`)
                            .then(setDetail)
                            .catch((e) => setError(String(e.message ?? e)))
                    }
                  >
                    {detail?.id === code.id
                      ? 'Fermer'
                      : code.personalTemplate
                        ? 'Codes émis'
                        : 'Qui l’a utilisé'}
                  </button>

                  {canWrite && code.active && (
                    <button
                      className="ap-btn"
                      disabled={saving}
                      onClick={() => {
                        const message =
                          code.kind === 'DISCOUNT'
                            ? `Fermer « ${code.code} » ? La remise est supprimée chez Stripe et le code ne pourra PAS être rouvert. Les abonnements qui en bénéficient déjà la conservent.`
                            : `Fermer « ${code.code} » ? Il ne pourra plus être réclamé. Les accès déjà ouverts courent jusqu’à leur terme.`;
                        if (window.confirm(message)) {
                          agir(
                            () =>
                              call(`/admin/promo-codes/${code.id}`, {
                                method: 'PATCH',
                                body: JSON.stringify({ active: false }),
                              }),
                            `Code « ${code.code} » fermé.`,
                          );
                        }
                      }}
                    >
                      Fermer
                    </button>
                  )}

                  {canWrite && utilise === 0 && emis === 0 && (
                    <button
                      className="ap-btn danger"
                      disabled={saving}
                      onClick={() => {
                        if (window.confirm(`Supprimer « ${code.code} » ? Il n’a jamais servi.`)) {
                          agir(
                            () => call(`/admin/promo-codes/${code.id}`, { method: 'DELETE' }),
                            `Code « ${code.code} » supprimé.`,
                          );
                        }
                      }}
                    >
                      Supprimer
                    </button>
                  )}
                </div>

                {detail?.id === code.id && detail.personalTemplate && (
                  <div className="ap-promo-detail">
                    {(detail.personalCodes ?? []).length === 0 ? (
                      <p className="ap-muted small">Aucun code émis pour l’instant.</p>
                    ) : (
                      <ul>
                        {detail.personalCodes.map((c) => (
                          <li key={c.id}>
                            <code>{c.code}</code> — {c.restrictedEmail}{' '}
                            <span className="ap-muted">
                              — émis le {dateCourte(c.createdAt)} ·{' '}
                              {c.redeemedCount > 0
                                ? 'activé'
                                : c.expiresAt && new Date(c.expiresAt) <= new Date()
                                  ? 'expiré sans activation'
                                  : `à activer avant le ${dateCourte(c.expiresAt)}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {detail?.id === code.id && !detail.personalTemplate && (
                  <div className="ap-promo-detail">
                    {detail.redemptions.length === 0 ? (
                      <p className="ap-muted small">Personne ne l’a encore utilisé.</p>
                    ) : (
                      <ul>
                        {detail.redemptions.map((r) => (
                          <li key={r.id}>
                            {r.space.name} <span className="ap-muted">— {dateCourte(r.createdAt)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AdminPromoCodes;
